import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from "motion/react";
import PremiumLoader from '@/components/PremiumLoader';
import { formatCurrencyForDisplay } from '@/lib/utils';

// Hook
import { useTransfer } from '@/features/booking/hooks/useTransfer';


// Components
import { TransferPageHero } from '@/features/booking/components/TransferPageHero';
import { TransferStatusPanel } from '@/features/booking/components/TransferStatusPanel';
import { TransferUnauthView } from '@/features/booking/components/TransferUnauthView';
import { TransferAddOnModal } from '@/features/booking/components/TransferAddOnModal';
import { TransferBookingCard } from '@/features/booking/components/TransferBookingCard';

export const TransferBookingPage: React.FC = () => {
    const { bookingId, transferId } = useParams<{ bookingId?: string; transferId?: string }>();
    const navigate = useNavigate();

    // Use the extracted hook
    const transfer = useTransfer(bookingId, transferId);

    // Local UI state for add-on modal
    const [selectedAddon, setSelectedAddon] = useState<any | null>(null);

    // --- Render States ---
    if (transfer.loading) return <PremiumLoader loading={true} />;
    if (transfer.viewMode === 'UNAUTH') return <TransferUnauthView />;
    if (transfer.viewMode === 'ERROR') {
        return (
            <div className="min-h-screen bg-black text-white flex items-center justify-center">
                {transfer.error || "Error loading page"}
            </div>
        );
    }
    if (transfer.viewMode === 'COMPLETED_THIRD_PARTY') {
        return (
            <div className="min-h-screen bg-black text-white flex flex-col items-center justify-center p-6 text-center">
                <motion.div
                    initial={{ opacity: 0, scale: 0.9, y: 20 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    transition={{ duration: 0.4, ease: "easeOut" }}
                    className="bg-gray-900 p-8 rounded-2xl shadow-lg shadow-green-500/5 border border-gray-800 max-w-sm w-full"
                >
                    <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ delay: 0.2, type: "spring", stiffness: 200, damping: 15 }}
                        className="w-16 h-16 mx-auto mb-5 rounded-full bg-gradient-to-br from-green-500/20 to-emerald-500/10 flex items-center justify-center border border-green-500/30"
                    >
                        <svg className="w-8 h-8 text-green-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                            <path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </motion.div>
                    <motion.h2
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3 }}
                        className="text-2xl font-serif font-extrabold text-white mb-3"
                    >
                        Transfer Completed
                    </motion.h2>
                    <motion.p
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.4 }}
                        className="text-gray-400 mb-6 text-sm leading-relaxed"
                    >
                        This transfer has already been processed and the ticket has been transferred to its new owner.
                    </motion.p>
                    <motion.button
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.5 }}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => navigate('/')}
                        className="w-full py-3 rounded-xl bg-violet-600 text-white font-bold hover:bg-violet-500 transition-colors"
                    >
                        Go to Home
                    </motion.button>
                </motion.div>
            </div>
        );
    }

    // Safety check
    if (!transfer.transferData || !transfer.bookingDetails) return null;

    // --- Derived Data from Booking Details ---
    const bookingDetails = transfer.bookingDetails;
    const eventBookingCategory = bookingDetails.event_booking_category;
    const eventSchedule = eventBookingCategory?.event_schedule;
    const event = eventSchedule?.event;
    const bookingCategory = eventBookingCategory?.booking_category;
    const startTime = bookingDetails.start_time;
    const addons = bookingDetails.add_ons || [];

    // Business Details
    const businessObj = bookingCategory?.business;
    const businessHandle = businessObj?.handle;
    const businessName = businessObj?.name;
    const businessDescription = businessObj?.description;
    const businessImageId = businessObj?.card_image_id || businessObj?.image_id;
    // Transform media array to match getBestImageUrl expected format
    const businessMedia = businessObj?.media?.map((m: { id: string; media_type: string }) => ({ 
        media_id: m.id, 
        rank: 0 
    })) || null;

    // Business Address
    let businessAddress = "";
    if (businessObj?.location) {
        const { address_line1, city, state_province, country } = businessObj.location;
        businessAddress = [address_line1, city, state_province, country].filter(Boolean).join(", ");
    }
    // Timezone from booking object (primary) or business location (fallback) - no default
    const businessTimeZone = bookingDetails.timezone_id || businessObj?.location?.time_zone;

    // Currency from event booking category - no fallback
    const currency = (eventBookingCategory as any)?.currency;
    const formatPrice = (amount: number) => currency ? formatCurrencyForDisplay(amount, currency) : `${amount / 100}`;

    // Table booking details
    const tableDetails = {
        tableNumber: bookingDetails.booked_table_number,
        tableSize: bookingDetails.booked_table_size,
        bookingTime: bookingDetails.start_time,
        slotDuration: eventBookingCategory?.custom_slot_duration,
    };

    // Navigation handlers
    const handleViewEvent = () => {
        if (businessHandle && (event?.id || eventSchedule?.event_id)) {
            navigate(`/venues/${businessHandle}/events/${event?.id || eventSchedule?.event_id}`);
        }
    };

    const handleCategoryClick = () => {
        const ebcId = eventBookingCategory?.id;
        if (ebcId) navigate(`/booking/tickets/${ebcId}`);
    };

    return (
        <div className="min-h-screen bg-black text-gray-200 font-sans">
            {/* Full-width Hero Section */}
            <TransferPageHero
                businessName={businessName}
                businessDescription={businessDescription}
                businessAddress={businessAddress}
                businessHandle={businessHandle}
                businessImageId={businessImageId}
                businessMedia={businessMedia}
                role={transfer.role}
                onBack={() => navigate(-1)}
            />

            {/* Warnings Section */}
            {transfer.isPending && (
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
                    <div className={`p-4 rounded-xl flex gap-3 text-sm ${transfer.role === 'SENDER' ? 'bg-yellow-500/10 text-yellow-500/80 border border-yellow-500/20' : 'bg-blue-500/10 text-blue-400 border border-blue-500/20'}`}>
                        <svg className="w-5 h-5 flex-shrink-0 mt-0.5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/>
                            <path d="M12 9v4"/>
                            <path d="M12 17h.01"/>
                        </svg>
                        <p>
                            {transfer.role === 'SENDER'
                                ? "Only share with friends you know. Don't share this link with an unknown person. Anyone can accept it and you will lose the ticket."
                                : "Only accept transfers from people you trust. Avoid paying strangers manually. If the event is cancelled, the refund will go to the original purchaser, not the current holder."}
                        </p>
                    </div>
                </div>
            )}

            {/* Main Content - Two Column Layout */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
                <div className="flex flex-col lg:flex-row gap-6">

                    {/* LEFT COLUMN: Booking Card */}
                    <div className="flex-1 flex flex-col gap-5 min-w-0">
                        <TransferBookingCard
                            event={event}
                            eventSchedule={eventSchedule}
                            bookingCategory={bookingCategory}
                            startTime={startTime}
                            addons={addons}
                            tableDetails={tableDetails}
                            businessTimeZone={businessTimeZone}
                            currency={currency}
                            onViewEvent={handleViewEvent}
                            onCategoryClick={handleCategoryClick}
                            onAddonClick={setSelectedAddon}
                        />
                    </div>

                    {/* RIGHT COLUMN: Transfer Status + Actions */}
                    <TransferStatusPanel
                        role={transfer.role}
                        displayStatus={transfer.displayStatus}
                        isPending={transfer.isPending}
                        isCompleted={transfer.isCompleted}
                        countdown={transfer.countdown}
                        copied={transfer.copied}
                        isAccepting={transfer.isAccepting}
                        bookingPrice={eventBookingCategory?.price ? formatPrice(eventBookingCategory.price) : undefined}
                        sender={transfer.transferData?.sender}
                        receiver={transfer.transferData?.receiver}
                        onCopyLink={transfer.handleCopyLink}
                        onCancelClick={() => transfer.setShowCancelModal(true)}
                        onAccept={transfer.handleAccept}
                        onViewTicket={() => transfer.handleNavigateToBooking(bookingId || (transfer.transferData?.booking as any)?.id)}
                    />
                </div>
            </div>

            {/* Cancel Modal */}
            <AnimatePresence>
                {transfer.showCancelModal && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                        <div className="bg-gray-900 border border-gray-700 rounded-2xl p-6 shadow-2xl max-w-sm w-full">
                            <h2 className="text-xl font-bold text-white mb-2">Cancel Transfer?</h2>
                            <p className="text-gray-400 text-sm mb-6">This will invalidate the link. Cannot be undone.</p>
                            <div className="flex gap-3">
                                <button onClick={() => transfer.setShowCancelModal(false)} className="flex-1 py-2 bg-gray-800 text-white rounded-lg">Back</button>
                                <button onClick={transfer.handleCancel} className="flex-1 py-2 bg-red-600 text-white rounded-lg">Confirm</button>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Add-on Details Modal */}
            <TransferAddOnModal
                isOpen={!!selectedAddon}
                onClose={() => setSelectedAddon(null)}
                addon={selectedAddon}
            />
        </div>
    );
};

export default TransferBookingPage;